import { Injectable } from '@angular/core';
import { capTask } from '@capacitor-community/sqlite';
import { OfflineDatabaseService } from './offline-database.service';
import {
  createOfflineUuid,
  OfflineSyncOperation,
  OfflineSyncOperationInput,
  OfflineSyncQueueSummary,
} from './offline-database.models';

interface SyncQueueRow extends Record<string, unknown> {
  id: number;
  operation_uuid: string;
  module: string;
  entity_type: string;
  entity_uuid: string;
  action: OfflineSyncOperation['action'];
  payload_json: string;
  dependency_uuid: string | null;
  status: OfflineSyncOperation['status'];
  attempts: number;
  available_at: string;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

@Injectable({ providedIn: 'root' })
export class SyncQueueRepository {
  constructor(private database: OfflineDatabaseService) {}

  async enqueue(input: OfflineSyncOperationInput): Promise<OfflineSyncOperation> {
    return this.enqueueWithLocalChanges([], input);
  }

  async enqueueWithLocalChanges(
    localTasks: capTask[],
    input: OfflineSyncOperationInput,
  ): Promise<OfflineSyncOperation> {
    const module = this.requiredText(input.module, 'módulo');
    const entityType = this.requiredText(input.entityType, 'tipo da entidade');
    const entityUuid = this.requiredText(input.entityUuid, 'UUID da entidade');
    const operationUuid = String(input.operationUuid || createOfflineUuid());
    const now = new Date().toISOString();
    const payloadJson = JSON.stringify(input.payload ?? null);

    await this.database.executeTransaction([
      ...localTasks,
      {
        statement: `INSERT OR IGNORE INTO sync_queue (
          operation_uuid, module, entity_type, entity_uuid, action, payload_json,
          dependency_uuid, status, attempts, available_at, created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, 'pendente', 0, ?, ?, ?)`,
        values: [
          operationUuid,
          module,
          entityType,
          entityUuid,
          input.action,
          payloadJson,
          input.dependencyUuid || null,
          now,
          now,
          now,
        ],
      },
    ]);

    const operation = await this.findByUuid(operationUuid);
    if (!operation) {
      throw new Error('Não foi possível registrar a operação na fila offline.');
    }
    return operation;
  }

  async listPending(module: string, limit = 25): Promise<OfflineSyncOperation[]> {
    const rows = await this.database.query<SyncQueueRow>(
      `SELECT * FROM sync_queue
       WHERE module = ?
         AND status IN ('pendente', 'erro')
         AND available_at <= ?
         AND (
           dependency_uuid IS NULL
           OR dependency_uuid IN (SELECT operation_uuid FROM sync_queue WHERE status = 'concluida')
           OR NOT EXISTS (
             SELECT 1 FROM sync_queue dependency
             WHERE dependency.operation_uuid = sync_queue.dependency_uuid
           )
         )
       ORDER BY id ASC
       LIMIT ?`,
      [module, new Date().toISOString(), Math.max(1, Math.min(100, Math.trunc(limit)))],
    );
    return rows.map((row) => this.mapRow(row));
  }

  async markProcessing(operationUuid: string): Promise<boolean> {
    const now = new Date().toISOString();
    const result = await this.database.run(
      `UPDATE sync_queue
       SET status = 'processando', updated_at = ?
       WHERE operation_uuid = ? AND status IN ('pendente', 'erro')`,
      [now, operationUuid],
    );
    return Number(result.changes?.changes || 0) > 0;
  }

  async markCompleted(operationUuid: string): Promise<void> {
    const now = new Date().toISOString();
    await this.database.run(
      `UPDATE sync_queue
       SET status = 'concluida', last_error = NULL, updated_at = ?
       WHERE operation_uuid = ?`,
      [now, operationUuid],
    );
  }

  async markFailure(operation: OfflineSyncOperation, error: unknown): Promise<void> {
    const attempts = operation.attempts + 1;
    const delaySeconds = Math.min(900, 5 * Math.pow(2, Math.min(attempts - 1, 8)));
    const availableAt = new Date(Date.now() + delaySeconds * 1000).toISOString();
    const now = new Date().toISOString();
    const message = this.errorMessage(error);

    const tasks: capTask[] = [
      {
        statement: `UPDATE sync_queue
          SET status = 'erro', attempts = ?, available_at = ?, last_error = ?, updated_at = ?
          WHERE operation_uuid = ?`,
        values: [attempts, availableAt, message, now, operation.operationUuid],
      },
      {
        statement: `INSERT INTO sync_errors (
          operation_uuid, module, error_code, message, details_json, created_at
        ) VALUES (?, ?, ?, ?, ?, ?)`,
        values: [operation.operationUuid, operation.module, null, message, null, now],
      },
    ];
    await this.database.executeTransaction(tasks);
  }

  async markConflict(
    operation: OfflineSyncOperation,
    reason: string,
    serverPayload: unknown,
    localPayload: unknown,
  ): Promise<void> {
    const now = new Date().toISOString();
    await this.database.executeTransaction([
      {
        statement: `UPDATE sync_queue
          SET status = 'conflito', last_error = ?, updated_at = ?
          WHERE operation_uuid = ?`,
        values: [reason, now, operation.operationUuid],
      },
      {
        statement: `INSERT INTO sync_conflicts (
          operation_uuid, module, entity_type, entity_uuid, local_payload_json,
          server_payload_json, reason, resolved, created_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, 0, ?)`,
        values: [
          operation.operationUuid,
          operation.module,
          operation.entityType,
          operation.entityUuid,
          JSON.stringify(localPayload ?? operation.payload ?? null),
          JSON.stringify(serverPayload ?? null),
          reason,
          now,
        ],
      },
    ]);
  }

  async restoreInterrupted(): Promise<void> {
    const now = new Date().toISOString();
    await this.database.run(
      `UPDATE sync_queue
       SET status = 'pendente', available_at = ?, updated_at = ?
       WHERE status = 'processando'`,
      [now, now],
    );
  }

  async getSummary(): Promise<OfflineSyncQueueSummary> {
    const rows = await this.database.query<Record<string, unknown>>(
      `SELECT
        SUM(CASE WHEN status = 'pendente' THEN 1 ELSE 0 END) AS pending,
        SUM(CASE WHEN status = 'processando' THEN 1 ELSE 0 END) AS processing,
        SUM(CASE WHEN status = 'erro' THEN 1 ELSE 0 END) AS errors,
        SUM(CASE WHEN status = 'conflito' THEN 1 ELSE 0 END) AS conflicts
       FROM sync_queue`,
    );
    const row = rows[0] || {};
    return {
      pending: Number(row['pending'] || 0),
      processing: Number(row['processing'] || 0),
      errors: Number(row['errors'] || 0),
      conflicts: Number(row['conflicts'] || 0),
    };
  }

  async getCursor(scope: string): Promise<string | null> {
    const rows = await this.database.query<Record<string, unknown>>(
      'SELECT cursor FROM sync_state WHERE scope = ? LIMIT 1',
      [scope],
    );
    return rows.length ? String(rows[0]['cursor'] || '') || null : null;
  }

  async updatePullState(scope: string, cursor: string | null): Promise<void> {
    const now = new Date().toISOString();
    await this.database.run(
      `INSERT INTO sync_state (scope, cursor, last_pulled_at, updated_at)
       VALUES (?, ?, ?, ?)
       ON CONFLICT(scope) DO UPDATE SET
         cursor = excluded.cursor,
         last_pulled_at = excluded.last_pulled_at,
         updated_at = excluded.updated_at`,
      [scope, cursor, now, now],
    );
  }

  async updatePushState(scope: string): Promise<void> {
    const now = new Date().toISOString();
    await this.database.run(
      `INSERT INTO sync_state (scope, last_pushed_at, updated_at)
       VALUES (?, ?, ?)
       ON CONFLICT(scope) DO UPDATE SET
         last_pushed_at = excluded.last_pushed_at,
         updated_at = excluded.updated_at`,
      [scope, now, now],
    );
  }

  async registerGeneralError(module: string, error: unknown): Promise<void> {
    const message = this.errorMessage(error);
    await this.database.run(
      `INSERT INTO sync_errors (module, message, created_at) VALUES (?, ?, ?)`,
      [module, message, new Date().toISOString()],
    );
  }

  async purgeCompleted(retentionDays = 7): Promise<void> {
    const cutoff = new Date(Date.now() - Math.max(1, retentionDays) * 86400000).toISOString();
    await this.database.run(
      `DELETE FROM sync_queue WHERE status = 'concluida' AND updated_at < ?`,
      [cutoff],
    );
  }

  private async findByUuid(operationUuid: string): Promise<OfflineSyncOperation | null> {
    const rows = await this.database.query<SyncQueueRow>(
      'SELECT * FROM sync_queue WHERE operation_uuid = ? LIMIT 1',
      [operationUuid],
    );
    return rows.length ? this.mapRow(rows[0]) : null;
  }

  private mapRow(row: SyncQueueRow): OfflineSyncOperation {
    let payload: unknown = null;
    try {
      payload = JSON.parse(String(row.payload_json || 'null'));
    } catch {
      payload = null;
    }
    return {
      id: Number(row.id),
      operationUuid: String(row.operation_uuid),
      module: String(row.module),
      entityType: String(row.entity_type),
      entityUuid: String(row.entity_uuid),
      action: row.action,
      payload,
      dependencyUuid: row.dependency_uuid ? String(row.dependency_uuid) : null,
      status: row.status,
      attempts: Number(row.attempts || 0),
      availableAt: String(row.available_at),
      lastError: row.last_error ? String(row.last_error) : null,
      createdAt: String(row.created_at),
      updatedAt: String(row.updated_at),
    };
  }

  private requiredText(value: unknown, field: string): string {
    const normalized = String(value || '').trim();
    if (!normalized) throw new Error(`Informe o ${field} da operação offline.`);
    return normalized;
  }

  private errorMessage(error: unknown): string {
    if (error instanceof Error) return error.message.slice(0, 2000);
    if (typeof error === 'string') return error.slice(0, 2000);
    try {
      return JSON.stringify(error).slice(0, 2000);
    } catch {
      return 'Falha desconhecida durante a sincronização.';
    }
  }
}
